import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";
import { api, apiError } from "../api";
import { useAuth } from "../auth";
import { usePricing } from "../usePricing";
import { Spinner, Toast, useToast } from "../components/ui";

const FEATURES = [
  "Up to 5 monitoring profiles",
  "Daily scheduled scans",
  "Manual and custom-date scans",
  "30-day scan history",
  "Browser push and email alerts",
  "CSV product import",
  "JSON data export",
];

export default function Pricing() {
  const { user, logout } = useAuth();
  const pricing = usePricing();
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      show(apiError(err), "error");
      setLoading(false);
    }
  };

  const subscribed = user?.subscription_status === "active";
  const onTrial = user?.subscription_status === "trialing" && user?.has_access;
  const trialDays = user?.trial_days_left ?? 0;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <Toast toast={toast} onClose={close} />
      <div className="w-full max-w-md fade-in">
        <div className="flex items-center gap-2.5 mb-6 justify-center">
          <div className="w-10 h-10 rounded-card bg-teal flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div className="font-display font-bold text-xl text-ink">PFU Tracker</div>
        </div>

        {onTrial && (
          <div className="card p-4 mb-4 bg-emerald-light border-emerald/30 text-center" data-testid="trial-banner">
            <p className="text-sm text-emerald font-medium">
              Free trial active — {trialDays} day{trialDays === 1 ? "" : "s"} left. Subscribe now or keep using Pro until it ends.
            </p>
          </div>
        )}
        {!subscribed && !onTrial && user && (
          <div className="card p-4 mb-4 bg-amber-light border-amber/30 text-center" data-testid="trial-ended-banner">
            <p className="text-sm text-amber font-medium">Your free trial has ended. Subscribe to keep monitoring prices.</p>
          </div>
        )}

        <div className="card overflow-hidden" data-testid="pricing-card">
          <div className="bg-teal text-white p-6">
            <div className="chip bg-white/15 text-white mb-3">Pro plan</div>
            <div className="flex items-end gap-2">
              <span className="font-display text-4xl font-bold">{pricing.display}</span>
              <span className="text-white/80 mb-1">/{pricing.period}</span>
            </div>
            <p className="text-white/80 text-sm mt-2">Billed monthly in {pricing.currency}.</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[15px] text-ink">
                  <span className="w-5 h-5 rounded-full bg-emerald-light flex items-center justify-center mt-0.5 shrink-0">
                    <Check className="w-3.5 h-3.5 text-emerald" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            {subscribed ? (
              <button className="btn-primary w-full" onClick={() => navigate("/dashboard")} data-testid="pricing-continue">
                Go to dashboard
              </button>
            ) : (
              <>
                <button className="btn-accent w-full" onClick={subscribe} disabled={loading} data-testid="pricing-subscribe">
                  {loading && <Spinner />} Subscribe — {pricing.display}/{pricing.period}
                </button>
                {onTrial && (
                  <button className="btn-ghost w-full mt-2" onClick={() => navigate("/dashboard")} data-testid="pricing-continue-trial">
                    Continue on free trial
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <button onClick={() => { logout(); navigate("/login"); }} className="text-sm text-muted mt-5 mx-auto block" data-testid="pricing-logout">
          Sign out
        </button>
      </div>
    </div>
  );
}
